import BreadcrumMenu from 'organization/theme/Breadcrum'
import React, {useMemo} from 'react'
import styled from 'styled-components'
import {toast} from 'react-toastify'
import Pagination from '@mui/material/Pagination/Pagination'
import {useOrganization} from 'organization/providers/OrganizationProvider'
import OrganizationPage from 'organization/theme/OrganizationPage'
import {useAffiliateEarning} from 'organization/AffiliateProgram/AffiliateEarningProvider'
import {ListBalance} from 'organization/AffiliateProgram/ListBalance'
import {OrgFaqHelp} from 'organization/Help/FAQ'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import {useI18n} from 'i18n/I18nContext'
import {SITE_ORIGIN} from 'seo/siteMeta'
import {getAffiliatePromoKits} from 'organization/AffiliateProgram/affiliatePromoKits'

export const affiliateStorageKey = 'affiliate_token'

export const AffiliateProgram = () => {
  const {organization} = useOrganization()
  const {t, locale} = useI18n()

  const affiliateLink = `${SITE_ORIGIN}/p/${organization?.affiliate_link.token}`

  const {ticketSales, balance, totalPages, page, changePage} =
    useAffiliateEarning()

  const {emails, socials, blogs} = useMemo(
    () => getAffiliatePromoKits(locale, affiliateLink),
    [locale, affiliateLink],
  )

  const copyToClipboard = (text: string, successMessage: string) => {
    navigator.clipboard.writeText(text)
    toast.success(successMessage)
  }

  return (
    <OrganizationPage>
      <div className="row pl-30 pb-30 pr-30">
        <>
          <BreadcrumMenu
            title={t('org.menu.affiliate')}
            helpInfo={<OrgFaqHelp faqKey="affiliate" />}
          />

          {/* LINK & STATS SECTION */}
          <div className="create__event-area mb-20 col-12">
            <div className="body__card-wrapper attendant__wrapper">
              <div className="row">
                <div className="col-12 mb-15">
                  <p>
                    <b>{t('org.affiliate.partnerLinkTitle')}</b>{' '}
                    <i>({t('org.affiliate.cookieNote')})</i>
                  </p>
                  <AffiliateHolder>
                    <LinkHolder className="singel__input-field">
                      <input type="text" disabled value={affiliateLink} />
                      <button
                        type="button"
                        onClick={() =>
                          copyToClipboard(
                            affiliateLink,
                            t('org.affiliate.copyLinkToast'),
                          )
                        }
                        className={'input__btn w-100 mt-30'}
                      >
                        {t('org.affiliate.copyLinkBtn')}
                      </button>
                    </LinkHolder>
                  </AffiliateHolder>

                  <Statistics>
                    <StatisticItem>
                      {t('org.affiliate.statOrgs')}
                      <br />
                      <b>
                        {organization?.affiliate_link.registered_organizations}
                      </b>
                    </StatisticItem>
                    <StatisticItem>
                      {t('org.affiliate.statEventsCreated')} <br />
                      <b>{organization?.affiliate_link.created_events}</b>
                    </StatisticItem>
                    <StatisticItem>
                      {t('org.affiliate.statEventsActive')} <br />
                      <b>{organization?.affiliate_link.active_events}</b>
                    </StatisticItem>
                  </Statistics>
                </div>
              </div>
            </div>
          </div>

          {/* TERMS INFO */}
          <div className="col-12 mb-20">
            <div className="create__event-area">
              <div className="body__card-wrapper attendant__wrapper">
                <h4 style={{marginBottom: 15}}>
                  {t('org.affiliate.termsTitle')}
                </h4>
                <p style={{marginBottom: 8}}>
                  {t('org.affiliate.termsLine1a')} <b>10 € + 5%</b>{' '}
                  {t('org.affiliate.termsLine1b')}
                </p>
                <p style={{marginBottom: 8}}>{t('org.affiliate.termsLine2')}</p>
                <p style={{marginBottom: 0}}>{t('org.affiliate.termsLine3')}</p>
              </div>
            </div>
          </div>

          {/* TRANSACTIONS & BALANCE */}
          <div className="col-12 mb-20">
            <div className="create__event-area">
              <div className="body__card-wrapper attendant__wrapper">
                <MainInfoHolder>
                  <div>
                    <p style={{fontSize: '1.5rem', margin: 0}}>
                      {t('org.affiliate.revenueTitle')}{' '}
                      <b style={{color: 'var(--clr-theme-1)'}}>
                        {ticketSales.toFixed(2)} €
                      </b>
                    </p>
                  </div>
                </MainInfoHolder>

                <h4 style={{marginBottom: 15}}>
                  {t('org.affiliate.txHistoryTitle')}
                </h4>
                <div style={{overflowX: 'auto'}}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t('orgTable.colNo')}</th>
                        <th>{t('orgTable.colDate')}</th>
                        <th>{t('orgTable.colType')}</th>
                        <th>{t('orgTable.colTicketTier')}</th>
                        <th>{t('orgTable.colDescription')}</th>
                        <th>{t('orgTable.colAmount')}</th>
                        <th>{t('orgTable.colPlatformFee')}</th>
                        <th>{t('orgTable.colYourShare')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {balance.length === 0 && (
                        <tr>
                          <td
                            colSpan={8}
                            style={{textAlign: 'center', padding: '20px'}}
                          >
                            {t('org.affiliate.noEarnings')}
                          </td>
                        </tr>
                      )}
                      {balance &&
                        balance.map((item, index) => (
                          <ListBalance
                            key={index}
                            balance={item}
                            index={index}
                          />
                        ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <PaginationHolder>
                    <Pagination
                      count={totalPages}
                      shape="rounded"
                      page={page}
                      onChange={changePage}
                    />
                  </PaginationHolder>
                )}
              </div>
            </div>
          </div>

          {/* PROMO MATERIALS SECTION (MOVED TO BOTTOM) */}
          <div className="col-12 mt-20">
            <div className="create__event-area">
              <div className="body__card-wrapper attendant__wrapper">
                {/* BIG BANNER FOR LOGOS AND GRAPHICS */}
                <BannerHolder className="mb-40">
                  <BannerContent>
                    <h3>{t('org.affiliate.bannerTitle')}</h3>
                    <p>{t('org.affiliate.bannerText')}</p>
                  </BannerContent>

                  <BannerButtonHolder>
                    <BannerButton
                      href="https://drive.google.com/file/d/1ob7mzNjBd00aVRSBmyY6YgwGPHlTv-Ko/view?usp=sharing"
                      target="_blank"
                    >
                      <CloudDownloadIcon /> {t('org.affiliate.downloadLogos')}
                    </BannerButton>
                    <BannerButton
                      href="https://drive.google.com/file/d/1AlOYoBbmwy66G6IsBgotfaw-yDb0Z9Aa/view?usp=sharing"
                      target="_blank"
                    >
                      <CloudDownloadIcon /> {t('org.affiliate.downloadBanners')}
                    </BannerButton>
                  </BannerButtonHolder>
                </BannerHolder>

                <h3 style={{marginBottom: 10}}>
                  {t('org.affiliate.promoTitle')}
                </h3>
                <p style={{opacity: 0.8, marginBottom: 30}}>
                  {t('org.affiliate.promoSubtitle')}
                </p>

                {/* EMAILS SECTION */}
                <SectionTitle>{t('org.affiliate.kitEmails')}</SectionTitle>
                <PromoGrid className="mb-40">
                  {emails.map((email, i) => (
                    <PromoCard key={i}>
                      <PromoHeader>
                        <b>{email.title}</b>
                        <CopyAction
                          onClick={() =>
                            copyToClipboard(
                              email.content,
                              t('org.affiliate.toastEmailCopied'),
                            )
                          }
                        >
                          <ContentCopyIcon fontSize="small" />{' '}
                          {t('org.affiliate.copy')}
                        </CopyAction>
                      </PromoHeader>
                      <PromoBody>{email.content}</PromoBody>
                    </PromoCard>
                  ))}
                </PromoGrid>

                {/* SOCIAL MEDIA SECTION */}
                <SectionTitle>{t('org.affiliate.kitSocial')}</SectionTitle>
                <PromoGrid className="mb-40">
                  {socials.map((social, i) => (
                    <PromoCard key={i}>
                      <PromoHeader>
                        <b>{social.title}</b>
                        <CopyAction
                          onClick={() =>
                            copyToClipboard(
                              social.content,
                              t('org.affiliate.toastPostCopied'),
                            )
                          }
                        >
                          <ContentCopyIcon fontSize="small" />{' '}
                          {t('org.affiliate.copy')}
                        </CopyAction>
                      </PromoHeader>
                      <PromoBody>{social.content}</PromoBody>
                    </PromoCard>
                  ))}
                </PromoGrid>

                {/* LONG BLOG POSTS SECTION */}
                <SectionTitle>{t('org.affiliate.kitBlog')}</SectionTitle>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                  }}
                >
                  {blogs.map((blog, i) => (
                    <BlogCard key={i}>
                      <PromoHeader>
                        <b>{blog.title}</b>
                        <CopyAction
                          onClick={() =>
                            copyToClipboard(
                              blog.content,
                              t('org.affiliate.toastArticleCopied'),
                            )
                          }
                        >
                          <ContentCopyIcon fontSize="small" />{' '}
                          {t('org.affiliate.copyLongForm')}
                        </CopyAction>
                      </PromoHeader>
                      <BlogBody>{blog.content}</BlogBody>
                    </BlogCard>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      </div>
    </OrganizationPage>
  )
}

/* ----------------------- styles ----------------------- */

const AffiliateHolder = styled.div`
  background: #f9f9f9;
  border: 1px dashed var(--clr-theme-1);
  border-radius: 8px;
  padding: 20px;
  width: 100%;
`

const LinkHolder = styled.div`
  display: flex;
  gap: 15px;
  justify-content: space-between;
  align-items: center;

  & > input {
    flex: 4;
    background: #fff;
    font-weight: bold;
    color: var(--clr-common-black);
  }

  & > button {
    margin: 0;
    flex: 1;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    & > input,
    & > button {
      width: 100%;
    }
  }
`

const MainInfoHolder = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--clr-border-1);
  padding-bottom: 25px;
  margin-bottom: 25px;
`

const PaginationHolder = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 30px;
`

const Statistics = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 25px;
  justify-content: space-between;
  flex-wrap: wrap;
`

const StatisticItem = styled.div`
  font-size: 1rem;
  padding: 18px 15px;
  background: #f5f5f5;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  text-align: center;
  flex: 1;
  min-width: 200px;

  & > b {
    font-size: 1.5rem;
    color: var(--clr-theme-1);
    display: block;
    margin-top: 5px;
  }
`

/* NEW STYLES FOR BANNER & PROMO MATERIALS */

const BannerHolder = styled.div`
  background: var(--clr-gradient-14);
  border-radius: 12px;
  padding: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #fff;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
`

const BannerContent = styled.div`
  flex: 1;
  padding-right: 20px;

  h3 {
    color: #fff;
    margin: 0 0 10px 0;
    font-weight: 900;
  }

  p {
    margin: 0;
    font-size: 1.05rem;
    opacity: 0.9;
    color: #fff;
  }

  @media (max-width: 768px) {
    padding-right: 0;
  }
`

const BannerButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  color: var(--clr-common-black);
  padding: 14px 24px;
  border-radius: 8px;
  font-weight: 900;
  text-decoration: none;
  font-size: 1.05rem;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 255, 255, 0.3);
    color: var(--clr-theme-1);
  }
`

const BannerButtonHolder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`

const SectionTitle = styled.h4`
  background: #f0f0f0;
  padding: 10px 15px;
  border-left: 4px solid var(--clr-theme-1);
  margin-bottom: 20px;
  border-radius: 0 4px 4px 0;
`

const PromoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
`

const PromoCard = styled.div`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const PromoHeader = styled.div`
  background: #f5f5f5;
  padding: 12px 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.95rem;
`

const CopyAction = styled.button`
  background: transparent;
  border: 1px solid var(--clr-theme-1);
  color: var(--clr-theme-1);
  font-weight: bold;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: var(--clr-theme-1);
    color: #fff;
  }
`

const PromoBody = styled.div`
  padding: 20px;
  font-size: 0.95rem;
  line-height: 1.6;
  white-space: pre-wrap;
  color: #444;
  background: #fafafa;
  flex-grow: 1;
`

const BlogCard = styled.div`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const BlogBody = styled.div`
  padding: 25px;
  font-size: 1rem;
  line-height: 1.8;
  white-space: pre-wrap;
  color: #333;
  background: #fff;
  max-height: 400px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }
`
